import { get, API_BASE_URL } from '@spark/services/http';
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useHistory } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { registerAuthContext } from './index';
import { getCognitoLogoutUrl } from '@/config/cognito';

const AuthContext = createContext();
registerAuthContext(AuthContext);

const SECRET_KEY = process.env.REACT_APP_SECRET_LINK_EXTERNAL;

const buildCognitoUser = oidcUser => ({
  ...oidcUser,
  name: oidcUser.profile?.name || oidcUser.profile?.email || 'Usuário',
  email: oidcUser.profile?.email || '',
  token: oidcUser.id_token,
  accessToken: oidcUser.access_token,
  idToken: oidcUser.id_token,
  refreshToken: oidcUser.refresh_token,
  isExternalUser: false,
  tipoAutenticacao: 'cognito',
});

const getStoredExternalUser = () => {
  const auth = localStorage.getItem('auth');

  if (!auth) {
    return null;
  }

  try {
    const authData = JSON.parse(auth);

    if (!authData?.token) {
      return null;
    }

    return {
      name: 'Usuário Externo',
      email: 'externo@transben.com.br',
      token: authData.token,
      accessToken: authData.token,
      isExternalUser: true,
      tipoAutenticacao: 'token_externo',
    };
  } catch (error) {
    console.error('Erro ao processar auth do localStorage:', error);
    localStorage.removeItem('auth');
    return null;
  }
};

const syncExternalAuthStorage = token => {
  if (token) {
    const payload = JSON.stringify({ token });
    localStorage.setItem('auth', payload);
    window.dispatchEvent(new StorageEvent('storage', { key: 'auth', newValue: payload }));
    return;
  }

  localStorage.removeItem('auth');
  window.dispatchEvent(new StorageEvent('storage', { key: 'auth', newValue: null }));
};

export const AuthProvider = ({ children }) => {
  const history = useHistory();
  const oidcAuth = useOidcAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    if (oidcAuth.isLoading) {
      setLoading(true);
      return;
    }

    if (oidcAuth.isAuthenticated && oidcAuth.user) {
      setUser(buildCognitoUser(oidcAuth.user));
      setLoading(false);
      return;
    }

    const externalUser = getStoredExternalUser();
    setUser(externalUser);

    if (!externalUser) {
      setUserProfile(null);
    }

    setLoading(false);
  }, [oidcAuth.isAuthenticated, oidcAuth.isLoading, oidcAuth.user]);

  useEffect(() => {
    const handleStorageChange = event => {
      if (event.key !== 'auth' || oidcAuth.isAuthenticated) {
        return;
      }

      const externalUser = getStoredExternalUser();
      setUser(externalUser);

      if (!externalUser) {
        setUserProfile(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [oidcAuth.isAuthenticated]);

  useEffect(() => {
    if (!user?.token || user?.isExternalUser) {
      setUserProfile(null);
    }
  }, [user?.isExternalUser, user?.token]);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await get(`${API_BASE_URL}/auth/profile`);

      if (response?.data?.user) {
        setUserProfile(response.data.user);
        return response.data.user;
      }
    } catch (error) {
      console.error('Erro ao buscar perfil do usuário:', error);
    }

    return null;
  }, []);

  const loginWithCredentials = useCallback(async () => {
    return {
      success: false,
      error: 'Login por credenciais desativado. Use o login via Cognito.',
    };
  }, []);

  const loginWithToken = useCallback(
    encryptedToken => {
      try {
        const decrypted = CryptoJS.AES.decrypt(encryptedToken, SECRET_KEY);
        const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
        let token;
        let userInfo = {};

        try {
          const parsed = JSON.parse(decryptedText);

          if (parsed.type === 'direct_access') {
            token = parsed.token;
            userInfo = {
              name: parsed.name || 'Usuário Externo',
              email: parsed.email || 'externo@transben.com.br',
              expiresAt: parsed.expiresAt,
            };
          } else {
            token = parsed?.token || decryptedText;
            userInfo = {
              name: parsed?.name || 'Usuário Externo',
              email: parsed?.email || 'externo@transben.com.br',
            };
          }
        } catch {
          token = decryptedText;
          userInfo = {
            name: 'Usuário Externo',
            email: 'externo@transben.com.br',
          };
        }

        const externalUser = {
          name: userInfo.name,
          email: userInfo.email,
          token,
          accessToken: token,
          isExternalUser: true,
          expiresAt: userInfo.expiresAt,
          tipoAutenticacao: 'token_externo',
        };

        syncExternalAuthStorage(token);
        setUser(externalUser);
        setUserProfile(null);
        return true;
      } catch (error) {
        console.error('Erro ao processar token criptografado:', error);
        return false;
      }
    },
    []
  );

  const logout = useCallback(async () => {
    localStorage.removeItem('processedToken');

    if (user?.isExternalUser) {
      syncExternalAuthStorage(null);
      setUser(null);
      setUserProfile(null);
      history.replace('/log_in');
      return;
    }

    try {
      await oidcAuth.removeUser();
    } catch (error) {
      console.warn('Falha ao limpar usuário local:', error);
    }

    setUser(null);
    setUserProfile(null);

    const hasHostedDomain = Boolean(process.env.REACT_APP_COGNITO_HOSTED_DOMAIN);
    const isCognitoHostedUi =
      hasHostedDomain || oidcAuth?.settings?.authority?.includes('amazoncognito.com');

    if (isCognitoHostedUi) {
      try {
        const logoutUrl = getCognitoLogoutUrl();
        window.location.assign(logoutUrl);
        return;
      } catch (error) {
        console.warn('Falha ao montar logout do Cognito:', error);
        window.location.assign('/log_in');
        return;
      }
    }

    if (oidcAuth.signoutRedirect) {
      try {
        await oidcAuth.signoutRedirect();
        return;
      } catch (error) {
        console.warn('Falha ao iniciar logout OIDC:', error);
      }
    }

    window.location.assign('/log_in');
  }, [history, oidcAuth, user?.isExternalUser]);

  const value = useMemo(() => ({
    user,
    userProfile,
    loading,
    isLoading: loading,
    isAuthenticated: Boolean(user),
    login: oidcAuth.signinRedirect,
    loginWithCredentials,
    loginWithToken,
    logout,
    fetchUserProfile,
  }), [
    user,
    userProfile,
    loading,
    oidcAuth.signinRedirect,
    loginWithCredentials,
    loginWithToken,
    logout,
    fetchUserProfile,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
